import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  Calendar,
  Users,
  FileText,
  Clock,
  Target,
  Award,
  CheckCircle,
  Loader2,
  AlertCircle,
  RefreshCw
} from 'lucide-react';

const getApiBaseUrl = (): string => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    return `${protocol}//${hostname}:8001`;
  }
  return 'http://localhost:8001';
};

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('proposalcraft_access_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

interface StatsData {
  total: number;
  in_progress: number;
  submitted: number;
  selected: number;
  success_rate: number;
}

interface MonthlyData {
  month: string;
  year: number;
  completed: number;
  started: number;
}

interface TypeData {
  type: string;
  count: number;
  percentage: number;
}

interface PerformanceData {
  completion_rate: number;
  average_completion_days: number;
  success_rate: number;
  total_proposals: number;
  completed_proposals: number;
  selected_proposals: number;
  in_progress_proposals: number;
}

const Analytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [stats, setStats] = useState<StatsData>({
    total: 0, in_progress: 0, submitted: 0, selected: 0, success_rate: 0,
  });
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [typeData, setTypeData] = useState<TypeData[]>([]);
  const [performance, setPerformance] = useState<PerformanceData>({
    completion_rate: 0, average_completion_days: 0, success_rate: 0,
    total_proposals: 0, completed_proposals: 0, selected_proposals: 0, in_progress_proposals: 0,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const baseUrl = getApiBaseUrl();
    const headers = getAuthHeaders();

    try {
      const [summaryRes, monthlyRes, typeRes, perfRes] = await Promise.all([
        fetch(`${baseUrl}/api/proposals/stats/summary`, { headers }),
        fetch(`${baseUrl}/api/proposals/stats/monthly?months=6`, { headers }),
        fetch(`${baseUrl}/api/proposals/stats/by-type`, { headers }),
        fetch(`${baseUrl}/api/proposals/stats/performance`, { headers }),
      ]);

      if (summaryRes.ok) setStats(await summaryRes.json());
      if (monthlyRes.ok) setMonthlyData(await monthlyRes.json());
      if (typeRes.ok) setTypeData(await typeRes.json());
      if (perfRes.ok) setPerformance(await perfRes.json());
    } catch (err: any) {
      console.error('Analytics fetch failed:', err);
      setError('통계 데이터를 불러오는데 실패했습니다. 서버 연결을 확인해주세요.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const maxMonthly = Math.max(...monthlyData.map(d => Math.max(d.completed, d.started)), 1);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-emerald-600 animate-spin" />
        <span className="ml-3 text-gray-600">통계 데이터를 불러오는 중...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">분석</h1>
          <p className="text-gray-600 mt-2">제안서 성과와 진행 상황을 분석하세요</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="h-4 w-4 mr-1" />
          새로고침
        </button>
      </div>

      {/* 에러 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* 주요 통계 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">총 제안서</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">선정</p>
              <p className="text-2xl font-bold text-gray-900">{stats.selected}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">작성 중</p>
              <p className="text-2xl font-bold text-gray-900">{stats.in_progress}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Target className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">성공률</p>
              <p className="text-2xl font-bold text-gray-900">{stats.success_rate}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* 성과 지표 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Target className="h-5 w-5 text-gray-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">완료율</p>
                <p className="text-xl font-bold text-gray-900">{performance.completion_rate}%</p>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Clock className="h-5 w-5 text-gray-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">평균 작성 기간</p>
                <p className="text-xl font-bold text-gray-900">
                  {performance.average_completion_days > 0 ? `${performance.average_completion_days}일` : '-'}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Award className="h-5 w-5 text-gray-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">선정률</p>
                <p className="text-xl font-bold text-gray-900">{performance.success_rate}%</p>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 bg-gray-100 rounded-lg">
                <FileText className="h-5 w-5 text-gray-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">진행 중</p>
                <p className="text-xl font-bold text-gray-900">{performance.in_progress_proposals}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 차트 영역 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 월별 제안서 추이 */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">월별 제안서 추이</h3>
          {monthlyData.length === 0 ? (
            <p className="text-center text-gray-500 py-8">데이터가 없습니다</p>
          ) : (
            <div className="space-y-4">
              {monthlyData.map((data, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <span className="w-16 text-sm text-gray-600">{data.month}</span>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-3">
                        <div
                          className="bg-green-500 h-3 rounded-full"
                          style={{ width: `${(data.completed / maxMonthly) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-8 text-right">
                        {data.completed}개
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 mt-1">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-500 h-2 rounded-full"
                          style={{ width: `${(data.started / maxMonthly) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-500 w-8 text-right">
                        {data.started}개
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex items-center space-x-4 pt-2 text-xs text-gray-500">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded" />
                  <span>완료</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-500 rounded" />
                  <span>시작</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 문서 타입별 분포 */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">문서 타입별 분포</h3>
          {typeData.length === 0 ? (
            <p className="text-center text-gray-500 py-8">데이터가 없습니다</p>
          ) : (
            <div className="space-y-4">
              {typeData.map((data, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-4 h-4 bg-emerald-500 rounded" />
                    <span className="text-sm font-medium text-gray-900">{data.type}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-emerald-500 h-2 rounded-full"
                        style={{ width: `${data.percentage}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-600 w-12 text-right">
                      {data.count}개
                    </span>
                    <span className="text-sm text-gray-500 w-12 text-right">
                      {data.percentage}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 하단 요약 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 완료율 시각화 */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">제안서 완료율</h3>
          <div className="text-center">
            <div className="relative inline-flex items-center justify-center w-24 h-24">
              <svg className="w-24 h-24 transform -rotate-90">
                <circle
                  cx="48" cy="48" r="36"
                  stroke="currentColor" strokeWidth="8" fill="transparent"
                  className="text-gray-200"
                />
                <circle
                  cx="48" cy="48" r="36"
                  stroke="currentColor" strokeWidth="8" fill="transparent"
                  strokeDasharray={`${2 * Math.PI * 36}`}
                  strokeDashoffset={`${2 * Math.PI * 36 * (1 - performance.completion_rate / 100)}`}
                  className="text-emerald-500"
                />
              </svg>
              <span className="absolute text-lg font-bold text-gray-900">{performance.completion_rate}%</span>
            </div>
            <p className="text-sm text-gray-600 mt-2">전체 제안서 중 완료 비율</p>
          </div>
        </div>

        {/* 제안서 상태 분포 */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">제안서 상태</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">선정</span>
              <div className="flex items-center space-x-2">
                <div className="w-16 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full"
                    style={{ width: `${stats.total > 0 ? (stats.selected / stats.total) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-sm font-medium">{stats.selected}개</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">제출</span>
              <div className="flex items-center space-x-2">
                <div className="w-16 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full"
                    style={{ width: `${stats.total > 0 ? (stats.submitted / stats.total) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-sm font-medium">{stats.submitted}개</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">작성중</span>
              <div className="flex items-center space-x-2">
                <div className="w-16 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-yellow-500 h-2 rounded-full"
                    style={{ width: `${stats.total > 0 ? (stats.in_progress / stats.total) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-sm font-medium">{stats.in_progress}개</span>
              </div>
            </div>
          </div>
        </div>

        {/* 성공률 */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">선정 성공률</h3>
          <div className="text-center">
            <div className="relative inline-flex items-center justify-center w-24 h-24">
              <svg className="w-24 h-24 transform -rotate-90">
                <circle
                  cx="48" cy="48" r="36"
                  stroke="currentColor" strokeWidth="8" fill="transparent"
                  className="text-gray-200"
                />
                <circle
                  cx="48" cy="48" r="36"
                  stroke="currentColor" strokeWidth="8" fill="transparent"
                  strokeDasharray={`${2 * Math.PI * 36}`}
                  strokeDashoffset={`${2 * Math.PI * 36 * (1 - performance.success_rate / 100)}`}
                  className="text-blue-500"
                />
              </svg>
              <span className="absolute text-lg font-bold text-gray-900">{performance.success_rate}%</span>
            </div>
            <p className="text-sm text-gray-600 mt-2">제출 대비 선정 비율</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
